import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const powerbi: Competitor = {
  "id": "powerbi",
  "name": "Microsoft Power BI",
  "group": "bi",
  "website": "https://www.microsoft.com/power-platform/products/power-bi",
  "owner": "Microsoft (now sold and run as the BI workload inside Microsoft Fabric)",
  "summary": "Microsoft's BI product: reports and semantic models are built mainly in the Windows-only Power BI Desktop app and shared through the Power BI service, which now sits inside Microsoft Fabric. It suits organizations already standardized on Microsoft 365, Azure and Entra ID.",
  "bestFor": [
    "Organizations already paying for Microsoft 365 E5 (Power BI Pro is included) or committed to Azure/Fabric",
    "Teams with DAX/Power Query skills who want a mature semantic modeling layer and a very large ecosystem of visuals, training and consultants",
    "Large internal audiences of read-only viewers, where an F64 or larger Fabric capacity lets viewers use free licenses",
    "Companies that need an on-premises option (Power BI Report Server)"
  ],
  "notFor": [
    "Mac-based authoring teams: Power BI Desktop requires Windows",
    "Business users who want to explore warehouse data in a spreadsheet-style grid with familiar formulas",
    "Snowflake-centric teams that want every view to query the warehouse live without managing import refreshes, model size limits or DirectQuery tuning",
    "Small teams with many viewers but no budget for F64-level capacity, since below F64 every viewer needs a Pro or PPU license"
  ],
  "strengths": [
    "Lower entry price per user: Pro is $14/user/month and Desktop is free to download, and Pro is bundled in Microsoft 365 E5",
    "At F64+ Fabric capacity (or legacy P1+), report viewers do not need a paid per-user license, which can be cheaper than per-seat pricing for very large viewer counts",
    "Deep Microsoft integration: Entra ID, Teams, Excel (Analyze in Excel), SharePoint, Azure and the wider Fabric platform (lakehouse, warehouse, Data Factory)",
    "Import mode with an in-memory engine can serve fast dashboards without running warehouse compute on every view",
    "Pixel-perfect paginated reports with scheduled delivery as PDF, PPTX, XLSX, DOCX or CSV",
    "On-premises deployment option through Power BI Report Server",
    "Very large community, third-party visuals marketplace and hiring pool"
  ],
  "limitations": [
    "Power BI Desktop, the main authoring and modeling tool, is Windows-only (Windows 10 / Server 2016 or later)",
    "Licensing is complex: Pro or PPU per user for creators everywhere and for viewers below F64; Fabric capacity (F SKUs) for capacity features; Premium P SKUs retired for new purchases since July 2024",
    "Copilot is not available with Pro or PPU alone; it requires a paid Fabric capacity of F2 or larger (or P1+). The standalone Copilot / Power BI agent experience is still in preview",
    "Writeback (translytical task flows) depends on Fabric User Data Functions and natively targets Fabric SQL databases, warehouses and lakehouses, not Snowflake directly",
    "Export limits: 30,000 rows to CSV and 150,000 rows to Excel from a visual; DirectQuery exports capped at 16 MB uncompressed",
    "Git integration for reports and semantic models in Fabric workspaces is still marked preview; PBIP project files must be managed with external git tooling from Desktop",
    "Python visuals in the service are limited to 1-minute execution, 30 MB payload and public PyPI packages",
    "Row-level security only filters workspace Viewers; Admin/Member/Contributor roles see all data, and service principals cannot be added to roles"
  ],
  "pricing": {
    "model": "per user (Pro / Premium Per User) plus optional capacity-based (Fabric F SKUs, Power BI Embedded)",
    "publicStartingPrice": "$14/user/month (Power BI Pro, billed annually); Premium Per User $24/user/month",
    "freeTier": "yes - free account and free Power BI Desktop for personal authoring; sharing requires Pro/PPU, or content on F64+ capacity for free-license viewers",
    "notes": "Pro rose from $10 to $14 and PPU from $20 to $24 on April 1, 2025 (first increase in about 10 years). Fabric capacity is billed per F SKU, pay-as-you-go (pausable) or as a 1-3 year reservation (~41% cheaper); Microsoft's Azure page did not render prices, so exact F SKU prices were not confirmed from a Microsoft source (third-party guide cites ~$0.18 per CU-hour and ~$5,000/month for a reserved F64). Key Fabric effect on cost: below F64, every viewer still needs Pro or PPU; at F64 and above, viewers with free licenses can consume content, but creators/publishers still need Pro. Copilot needs F2+ capacity. Embedding for external customers (app owns data) needs capacity (F, A or legacy P SKUs) and no per-viewer licenses. Pro is included in Microsoft 365 E5 / Office 365 E5.",
    "sourceUrl": "https://www.microsoft.com/power-platform/products/power-bi/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "partial",
      "note": "DirectQuery to Snowflake is supported (with Entra SSO), but Import mode is the default and many features and performance patterns assume imported data.",
      "sourceUrl": "https://learn.microsoft.com/en-us/power-bi/connect-data/desktop-connect-snowflake"
    },
    "spreadsheet_interface": {
      "value": "no",
      "note": "No spreadsheet-style formula grid; users build visuals and DAX measures. Analyze in Excel connects Excel to a semantic model as a separate tool.",
      "sourceUrl": "https://learn.microsoft.com/power-bi/enterprise/service-admin-rls"
    },
    "sql_editor": {
      "value": "unknown",
      "note": "Not verified for Power BI reports. Fabric warehouses have their own SQL tools, but that is outside Power BI itself.",
      "sourceUrl": ""
    },
    "python_support": {
      "value": "partial",
      "note": "Python visuals and Python scripts in Desktop; in the service, Python visuals limited to 1-minute runtime, 30 MB payload and supported PyPI packages.",
      "sourceUrl": "https://learn.microsoft.com/power-bi/connect-data/service-python-packages-support"
    },
    "writeback": {
      "value": "partial",
      "note": "Translytical task flows (GA) let report users add/edit/delete records via Fabric User Data Functions; native targets are Fabric SQL database, warehouse and lakehouse, so Snowflake writeback needs custom function code.",
      "sourceUrl": "https://powerbi.microsoft.com/blog/translytical-task-flows-generally-available/",
      "badge": "Fabric targets only"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "Semantic models with DAX measures, relationships, RLS and OLS are reusable across reports.",
      "sourceUrl": "https://learn.microsoft.com/power-bi/enterprise/service-admin-rls"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "Embed for your customers (app owns data) on F, A or P capacity; external users need no Power BI license.",
      "sourceUrl": "https://learn.microsoft.com/en-us/power-bi/guidance/powerbi-implementation-planning-usage-scenario-embed-for-your-customers"
    },
    "ai_assistant": {
      "value": "partial",
      "note": "Copilot report pane is GA but needs paid F2+ Fabric (or P1+) capacity, not Pro/PPU alone; standalone Copilot and app agent are preview.",
      "sourceUrl": "https://learn.microsoft.com/power-bi/create-reports/copilot-introduction"
    },
    "version_control_git": {
      "value": "partial",
      "note": "Fabric workspace git integration (Azure DevOps, GitHub, GitHub Enterprise) is GA as a feature, but report and semantic model items are still marked preview; Desktop PBIP files use external git.",
      "sourceUrl": "https://learn.microsoft.com/en-us/fabric/cicd/git-integration/intro-to-git-integration",
      "badge": "Preview"
    },
    "open_source_or_self_host": {
      "value": "partial",
      "note": "Not open source. Power BI Report Server is a self-hosted on-prem option, licensed via SQL Server Enterprise/Standard cores or Fabric F64+ reservations.",
      "sourceUrl": "https://learn.microsoft.com/en-us/power-bi/report-server/get-started"
    },
    "desktop_authoring_required": {
      "value": "partial",
      "note": "Power BI Desktop (Windows-only) is the primary tool for modeling and report design; web editing exists but is not the main path and some modeling features on the web are preview.",
      "sourceUrl": "https://learn.microsoft.com/is-is/power-bi/fundamentals/desktop-get-the-desktop"
    },
    "scheduled_delivery": {
      "value": "yes",
      "note": "Email subscriptions (hourly to monthly or after refresh); PDF/PPTX attachments on Premium/PPU workspaces; paginated reports add XLSX/DOCX/CSV. Email only per docs.",
      "sourceUrl": "https://learn.microsoft.com/en-gb/power-BI/collaborate-share/end-user-subscribe"
    },
    "row_level_security": {
      "value": "yes",
      "note": "DAX-based RLS roles for import and DirectQuery; applies only to workspace Viewers.",
      "sourceUrl": "https://learn.microsoft.com/power-bi/enterprise/service-admin-rls"
    },
    "excel_export": {
      "value": "yes",
      "note": "Export from visuals to CSV (30k rows) or Excel (150k rows; 16 MB cap with DirectQuery).",
      "sourceUrl": "https://learn.microsoft.com/power-bi/visuals/power-bi-visualization-export-data"
    }
  },
  "recentChanges": [
    {
      "date": "2024-07",
      "change": "Power BI Premium per-capacity (P SKU) purchases ended for new customers; existing non-EA customers had to move to Fabric F SKUs at renewals after Feb 1, 2025.",
      "sourceUrl": "https://powerbi.microsoft.com/blog/important-update-coming-to-power-bi-premium-licensing"
    },
    {
      "date": "2025-04",
      "change": "First Power BI price increase in about 10 years: Pro to $14 and Premium Per User to $24 per user per month.",
      "sourceUrl": "https://community.fabric.microsoft.com/t5/Power-BI-Updates-Blog/Important-update-to-Microsoft-Power-BI-pricing/ba-p/5174341"
    },
    {
      "date": "2025",
      "change": "Translytical task flows reached general availability, adding writeback and actions from reports via Fabric User Data Functions (exact GA date not stated on the page).",
      "sourceUrl": "https://powerbi.microsoft.com/blog/translytical-task-flows-generally-available/"
    },
    {
      "date": "2026-02",
      "change": "Copilot input limit raised to 10K characters; Fabric Copilot capacity tenant setting enabled by default from Feb 8, 2026; legacy Excel/CSV import in the service loses entry points May 31, 2026 and stops refreshing July 31, 2026.",
      "sourceUrl": "https://powerbi.microsoft.com/blog/power-bi-february-2026-feature-summary"
    },
    {
      "date": "2026-11",
      "change": "Copilot report pane is GA; standalone Power BI agent, app agent and Copilot in web modeling remain in preview (per docs as checked).",
      "sourceUrl": "https://learn.microsoft.com/power-bi/create-reports/copilot-introduction"
    }
  ],
  "sources": [
    {
      "title": "Power BI pricing (Microsoft)",
      "url": "https://www.microsoft.com/power-platform/products/power-bi/pricing"
    },
    {
      "title": "Important update to Microsoft Power BI pricing",
      "url": "https://community.fabric.microsoft.com/t5/Power-BI-Updates-Blog/Important-update-to-Microsoft-Power-BI-pricing/ba-p/5174341"
    },
    {
      "title": "Important update coming to Power BI Premium licensing",
      "url": "https://powerbi.microsoft.com/blog/important-update-coming-to-power-bi-premium-licensing"
    },
    {
      "title": "Translytical task flows generally available",
      "url": "https://powerbi.microsoft.com/blog/translytical-task-flows-generally-available/"
    },
    {
      "title": "Copilot in Power BI introduction",
      "url": "https://learn.microsoft.com/power-bi/create-reports/copilot-introduction"
    },
    {
      "title": "Power BI February 2026 feature summary",
      "url": "https://powerbi.microsoft.com/blog/power-bi-february-2026-feature-summary"
    },
    {
      "title": "Power BI end-user features and licensing",
      "url": "https://learn.microsoft.com/en-us/power-bi/fundamentals/end-user-features"
    },
    {
      "title": "Connect to Snowflake in Power BI",
      "url": "https://learn.microsoft.com/en-us/power-bi/connect-data/desktop-connect-snowflake"
    },
    {
      "title": "Fabric Git integration overview",
      "url": "https://learn.microsoft.com/en-us/fabric/cicd/git-integration/intro-to-git-integration"
    },
    {
      "title": "Get Power BI Desktop",
      "url": "https://learn.microsoft.com/is-is/power-bi/fundamentals/desktop-get-the-desktop"
    },
    {
      "title": "Python packages support in the Power BI service",
      "url": "https://learn.microsoft.com/power-bi/connect-data/service-python-packages-support"
    },
    {
      "title": "Row-level security with Power BI",
      "url": "https://learn.microsoft.com/power-bi/enterprise/service-admin-rls"
    },
    {
      "title": "Email subscriptions in the Power BI service",
      "url": "https://learn.microsoft.com/en-gb/power-BI/collaborate-share/end-user-subscribe"
    },
    {
      "title": "What is Power BI Report Server",
      "url": "https://learn.microsoft.com/en-us/power-bi/report-server/get-started"
    },
    {
      "title": "Export data from visualizations",
      "url": "https://learn.microsoft.com/power-bi/visuals/power-bi-visualization-export-data"
    },
    {
      "title": "Embed for your customers usage scenario",
      "url": "https://learn.microsoft.com/en-us/power-bi/guidance/powerbi-implementation-planning-usage-scenario-embed-for-your-customers"
    },
    {
      "title": "Fabric pricing explainer (third party, used only for approximate F SKU prices)",
      "url": "https://www.osedea.com/insight/how-much-does-microsoft-fabric-cost-pricing-and-licensing-explained"
    }
  ],
  "lastReviewed": "2026-11-03"
};
